import { Box, Table, TableContainer, Tbody, Th, Thead, Tr } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  getDashboardBlocks,
  getSelectedDashboardScenario,
  getCompareDashboardScenario,
} from 'redux/DashboardSliceV2';

import VarianceRow from './VarianceRows';

export interface VarianceProperties {
  output: any;
  columnSettings: any;
  columnPositions?: any;
}

const Variance = ({ output, columnSettings, columnPositions }: VarianceProperties) => {
  const [columns, setColumns] = useState<any>([]);
  const [rows, setRows] = useState<any>([]);
  const blockDetails = useSelector(getDashboardBlocks);
  const selectedDashboardScenario = useSelector(getSelectedDashboardScenario);
  const compareDashboardScenario = useSelector(getCompareDashboardScenario);

  const getDimensionNameFromId = (
    dimensionId: number | string,
    blockId: number | string,
    indicatorId: number | string,
  ): string | null => {
    if (!dimensionId || !blockId || !indicatorId || !Array.isArray(blockDetails)) {
      return null;
    }

    const foundBlock = blockDetails.find((block: any) => block.block_id === Number(blockId));
    if (!foundBlock) {
      return null;
    }

    const foundIndicator = foundBlock?.indicators?.find(
      (ind: any) => ind.indicator_id === Number(indicatorId),
    );
    if (!foundIndicator) {
      return null;
    }

    const foundDimension = foundIndicator?.indicator_dimensions?.find(
      (dim: any) => dim.indicator_dimension_id === Number(dimensionId),
    );

    return foundDimension?.indicator_dimension_name || null;
  };

  const extractColumns = (settings: any): string[] => {
    // Build the list of columns in the order they are enabled in settings.
    // The baseline/actual column is always present — it is not user-toggleable.
    const columnList: string[] = ['Indicators'];
    if (settings?.actual_column_name) {
      columnList.push(settings.actual_column_name);
    } else if (selectedDashboardScenario?.name) {
      columnList.push(selectedDashboardScenario.name);
    } else {
      columnList.push('Actual');
    }

    if (settings?.previous_year) columnList.push('Prior Year');
    if (settings?.varience_to_py) columnList.push('▲PY');
    if (settings?.varience_percentage_py) columnList.push('▲PY%');
    if (settings?.plan) {
      if (settings?.plan_comparison_column_name) {
        columnList.push(settings?.plan_comparison_column_name);
      } else if (compareDashboardScenario?.name) {
        columnList.push(compareDashboardScenario.name);
      } else {
        columnList.push('Comparison (PL)');
      }
    }
    // if (settings?.plan) columnList.push('Plan');
    if (settings?.varience_to_pl) columnList.push('▲PL');
    if (settings?.varience_percentage_pl) columnList.push('▲PL%');

    const colPos: Record<string, number> | undefined = columnPositions;
    if (colPos && typeof colPos === 'object') {
      // Map display column names to their position keys used in columnPositions
      const getPositionKey = (columnName: string): string => {
        // Direct matches (variance columns already use the correct keys)
        if (
          columnName === '▲PY' ||
          columnName === '▲PY%' ||
          columnName === '▲PL' ||
          columnName === '▲PL%'
        ) {
          return columnName;
        }
        // Map display names to position keys
        if (columnName === 'Prior Year') return 'PY';
        const planComparisonName =
          settings?.plan_comparison_column_name ||
          compareDashboardScenario?.name ||
          'Comparison (PL)';
        if (columnName === 'Comparison (PL)' || columnName === planComparisonName) {
          return 'Comparison (PL)';
        }
        // Actual column - map to 'Actual' position key
        const actualColumnName =
          settings?.actual_column_name || selectedDashboardScenario?.name || 'Actual';
        if (columnName === 'Actual' || columnName === actualColumnName) {
          return 'Actual';
        }
        // Return original name (will default to position 999)
        return columnName;
      };

      const [first, ...rest] = columnList;
      const sortedRest = rest.slice().sort((a, b) => {
        const keyA = getPositionKey(a);
        const keyB = getPositionKey(b);
        const posA = colPos[keyA] ?? 999;
        const posB = colPos[keyB] ?? 999;
        return posA - posB;
      });
      return [first, ...sortedRest];
    }
    return columnList;
  };
  const formatRowData = (rawData: any[], settings: any) => {
    if (!rawData || !Array.isArray(rawData)) return [];
    const data = JSON.parse(JSON.stringify(rawData));
    return data
      ?.sort((a: any, b: any) => {
        const positionA = a?.indicator?.position ?? 0;
        const positionB = b?.indicator?.position ?? 0;
        return positionA - positionB;
      })
      ?.map((row: any) => {
        // variance_presentation lives inside total[0] (not at the row top level),
        // so capture it from there before the spread to avoid overwriting with {}.
        const variancePresentation = { ...row.total[0]?.variance_presentation };
        return {
          indicator: row.indicator,
          isCollapsable: row.dim_df?.length > 0,
          isCollapsed: true,
          ...row.total[0],
          indicator_id: row.indicator.indicator_id,
          Indicators: row.indicator.indicator_name,
          variance_presentation: variancePresentation,
          children:
            row.dim_df && row.dim_df.length > 0
              ? row.dim_df
                  .map((r: any) => {
                    const dimensionName =
                      getDimensionNameFromId(
                        row.indicator.dimension_id,
                        row.indicator.block_id,
                        row.indicator.indicator_id,
                      ) || row.indicator.dimension_name;
                    return {
                      ...r,
                      indicator: row.indicator,
                      Indicators: r[dimensionName] || r[`${row.indicator.dimension_name}`],
                    };
                  })
                  .map((r: any) => {
                    const formattedRow: any = { ...r };
                    const childVariancePresentation = {
                      ...r.variance_presentation,
                    };
                    delete formattedRow.dim_df;
                    if (settings?.varience_to_py) {
                      formattedRow['▲PY'] = r.PY;
                      delete formattedRow.PY;
                    }
                    if (settings?.varience_percentage_py) {
                      formattedRow['▲PY%'] = r['PY%'];
                      delete formattedRow['PY%'];
                      // Keep variance presentation tokens aligned with the display column names.
                      if (childVariancePresentation['PY%']) {
                        childVariancePresentation['▲PY%'] = childVariancePresentation['PY%'];
                        delete childVariancePresentation['PY%'];
                      }
                    }
                    if (settings?.varience_to_pl) {
                      formattedRow['▲PL'] = r.PL;
                      delete formattedRow.PL;
                    }
                    if (settings?.varience_percentage_pl) {
                      formattedRow['▲PL%'] = r['PL%'];
                      delete formattedRow['PL%'];
                      // Keep variance presentation tokens aligned with the display column names.
                      if (childVariancePresentation['PL%']) {
                        childVariancePresentation['▲PL%'] = childVariancePresentation['PL%'];
                        delete childVariancePresentation['PL%'];
                      }
                    }

                    const actualColumnName =
                      settings?.actual_column_name || selectedDashboardScenario?.name || 'Actual';
                    formattedRow[actualColumnName] = r[actualColumnName] ?? r.Actual;
                    delete formattedRow.Actual;
                    if (
                      settings?.actual_column_name &&
                      actualColumnName !== settings?.actual_column_name
                    ) {
                      delete formattedRow[settings?.actual_column_name];
                    }

                    const planComparisonColumnName =
                      settings?.plan_comparison_column_name ||
                      compareDashboardScenario?.name ||
                      'Comparison (PL)';
                    // If custom column name is not set, always use data from 'Comparison' column (API response)
                    // If custom column name is set, try that first, then fall back to 'Comparison'
                    if (settings?.plan_comparison_column_name) {
                      formattedRow[planComparisonColumnName] =
                        r[planComparisonColumnName] ?? r.Comparison;
                    } else {
                      formattedRow[planComparisonColumnName] = r.Comparison;
                    }
                    delete formattedRow['Comparison (PL)'];
                    delete formattedRow.Comparison;
                    if (
                      settings?.plan_comparison_column_name &&
                      planComparisonColumnName !== settings?.plan_comparison_column_name
                    ) {
                      delete formattedRow[settings?.plan_comparison_column_name];
                    }
                    formattedRow.variance_presentation = childVariancePresentation;
                    return formattedRow;
                  })
              : [],
        };
      })
      .map((row: any) => {
        const formattedRow: any = { ...row };
        const variancePresentation = { ...row.variance_presentation };
        delete formattedRow.dim_df;
        if (settings?.varience_to_py) {
          formattedRow['▲PY'] = row.PY;
          delete formattedRow.PY;
        }
        if (settings?.varience_percentage_py) {
          formattedRow['▲PY%'] = row['PY%'];
          delete formattedRow['PY%'];
          // Keep variance presentation tokens aligned with the display column names.
          if (variancePresentation['PY%']) {
            variancePresentation['▲PY%'] = variancePresentation['PY%'];
            delete variancePresentation['PY%'];
          }
        }
        if (settings?.varience_to_pl) {
          formattedRow['▲PL'] = row.PL;
          delete formattedRow.PL;
        }
        if (settings?.varience_percentage_pl) {
          formattedRow['▲PL%'] = row['PL%'];
          delete formattedRow['PL%'];
          // Keep variance presentation tokens aligned with the display column names.
          if (variancePresentation['PL%']) {
            variancePresentation['▲PL%'] = variancePresentation['PL%'];
            delete variancePresentation['PL%'];
          }
        }

        const actualColumnName =
          settings?.actual_column_name || selectedDashboardScenario?.name || 'Actual';
        formattedRow[actualColumnName] = row[actualColumnName] ?? row.Actual;
        delete formattedRow.Actual;
        if (settings?.actual_column_name && actualColumnName !== settings?.actual_column_name) {
          delete formattedRow[settings?.actual_column_name];
        }

        const planComparisonColumnName =
          settings?.plan_comparison_column_name ||
          compareDashboardScenario?.name ||
          'Comparison (PL)';
        // If custom column name is not set, always use data from 'Comparison' column (API response)
        // If custom column name is set, try that first, then fall back to 'Comparison'
        if (settings?.plan_comparison_column_name) {
          formattedRow[planComparisonColumnName] = row[planComparisonColumnName] ?? row.Comparison;
        } else {
          formattedRow[planComparisonColumnName] = row.Comparison;
        }
        delete formattedRow['Comparison (PL)'];
        delete formattedRow.Comparison;
        if (
          settings?.plan_comparison_column_name &&
          planComparisonColumnName !== settings?.plan_comparison_column_name
        ) {
          delete formattedRow[settings?.plan_comparison_column_name];
        }

        formattedRow.variance_presentation = variancePresentation;
        return formattedRow;
      });
  };

  useEffect(() => {
    if (columnSettings && output) {
      const extractedColumns = extractColumns(columnSettings);
      setColumns(extractedColumns);
      const formattedRows = formatRowData(output.data, columnSettings);
      setRows(formattedRows);
    }
  }, [columnSettings, output, selectedDashboardScenario, compareDashboardScenario, blockDetails]);

  return (
    <Box borderRadius='10px' overflowY='auto' overflowX='auto' className='scroll'>
      <TableContainer
        overflowY='auto'
        overflowX='auto'
        mb={{ base: 4.75, md: 0 }}
        mt={{ base: 0, md: 0 }}
        borderRadius='10px'
        className='table_scroll indicator-table dashboard-drag-header'
        cursor='grab'
      >
        <Table variant='base'>
          <Thead height={'9'} zIndex={2}>
            <Tr>
              {columns.map((column: string) => (
                <Th
                  key={column}
                  textAlign={'center'}
                  fontFamily={'regular_roboto'}
                  fontSize={'xs'}
                  fontWeight={500}
                  textTransform='none'
                  title={column}
                >
                  {column === 'Indicators'
                    ? ''
                    : column?.length > 20
                    ? `${column.slice(0, 20)}...`
                    : column}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row: any) => (
              <VarianceRow key={row.indicator_id} row={row} columns={columns} />
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
export default Variance;
